import { useState } from "react"
import { useDraggable } from "@dnd-kit/core"
import { Popover, Tooltip } from "antd"
import { SyncOutlined } from "@ant-design/icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendarCheck } from "@fortawesome/pro-regular-svg-icons"
import { faSnowflake } from "@fortawesome/pro-solid-svg-icons"
import dayjs from "dayjs"
import { TaskCalendarEvent } from "../scheduleApi"
import TaskPopoverContent from "../TaskPopoverContent"
import { HOVER_POPOVER_OVERLAY_STYLE } from "../scheduleEventStyles"
import { getMonthCardStyles } from "./monthCardStyles"

export type Props = {
  event: TaskCalendarEvent
  onTaskClick?: (event: TaskCalendarEvent) => void
}

const UNASSIGNED_COLOR = '#e0e0e0'

const MonthViewTask = ({ event, onTaskClick }: Props) => {
  const color = event.crewForemanColor || UNASSIGNED_COLOR
  const isCompleted = !!event.completedAt

  const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
    id: `task-${event.id}`,
    data: { event, isTask: true },
    disabled: event.frozen,
  })

  const [isHovering, setIsHovering] = useState(false)

  const startTime = dayjs(event.start)

  const formatTime = () => {
    if (event.allDay) {
      return 'All Day'
    }
    return startTime.format('h:mmA')
  }

  const { outerStyle, innerStyle } = getMonthCardStyles({ color, isDragging, transform })

  return (
    <Popover
      content={<TaskPopoverContent event={event} />}
      open={isHovering && !isDragging}
      placement="right"
      overlayStyle={HOVER_POPOVER_OVERLAY_STYLE}
    >
      <div
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        onClick={(e) => {
          e.stopPropagation()
          if (onTaskClick) {
            onTaskClick(event)
          }
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        style={outerStyle}
      >
        <div style={innerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#999', fontSize: 11 }}>
            <FontAwesomeIcon icon={faCalendarCheck} style={{ fontSize: 11 }} />
            <span>TASK</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0, textDecoration: isCompleted ? 'line-through' : undefined, color: isCompleted ? '#999' : undefined }}>
              {event.title}
            </span>
            {event.frozen && (
              <Tooltip title="Frozen">
                <FontAwesomeIcon icon={faSnowflake} style={{ color: '#0958d9', fontSize: 11, flexShrink: 0 }} />
              </Tooltip>
            )}
            {event.repeatingId && (
              <SyncOutlined style={{ color: '#999', fontSize: '11px', flexShrink: 0 }} />
            )}
          </div>
          <span style={{ color: '#666', fontSize: 12, whiteSpace: 'nowrap' }}>{formatTime()}</span>
        </div>
      </div>
    </Popover>
  )
}

export default MonthViewTask
